import api from "./api";
import { createDefaultConteudosData, normalizeConteudosData } from "../data";
const getSnapshotFromResponse = (response) => {
  const payload = response?.data?.data;
  return {
    initialized: Boolean(payload?.initialized),
    snapshot: normalizeConteudosData(
      payload?.snapshot || createDefaultConteudosData(),
    ),
  };
};
export async function fetchConteudosState() {
  const response = await api.get("/conteudos");
  return getSnapshotFromResponse(response);
}

// Snapshot leve para o aluno: só categorias + cursos + progresso do aluno.
export async function fetchConteudosAluno() {
  const response = await api.get("/conteudos/aluno");
  return getSnapshotFromResponse(response);
}

// Módulos + aulas de um único curso, com o conteúdo completo (guias + blocos
// de HTML) de cada aula. Pesado — usado apenas pelo editor do admin.
export async function fetchCursoDetalhes(cursoId) {
  const response = await api.get("/conteudos/curso-detalhes", {
    params: {
      id: cursoId,
    },
  });
  const payload = response?.data?.data || {};
  return {
    modulos: Array.isArray(payload.modulos) ? payload.modulos : [],
    aulas: Array.isArray(payload.aulas) ? payload.aulas : [],
  };
}

// Versão leve de fetchCursoDetalhes: guias vêm só com id/nome, sem o
// conteúdo (blocos/html) de cada aula. Usada pelo aluno para listar
// módulos/aulas; o conteúdo de uma aula é buscado à parte, ao abri-la
// (fetchAulaDetalhes), evitando carregar o HTML de todo o curso de uma vez.
export async function fetchCursoEstrutura(cursoId) {
  const response = await api.get("/conteudos/curso-estrutura", {
    params: {
      id: cursoId,
    },
  });
  const payload = response?.data?.data || {};
  return {
    modulos: Array.isArray(payload.modulos) ? payload.modulos : [],
    aulas: Array.isArray(payload.aulas) ? payload.aulas : [],
  };
}

// Conteúdo completo (guias + blocos + anexos) de uma única aula, buscado
// quando o aluno efetivamente abre a aula na tela de lição.
export async function fetchAulaDetalhes(aulaId) {
  const response = await api.get("/conteudos/aula-detalhes", {
    params: {
      id: aulaId,
    },
  });
  return response?.data?.data || null;
}
export async function fetchConteudosInativos() {
  const response = await api.get("/conteudos/inativos");
  const payload = response?.data?.data || {};
  return {
    categorias: Array.isArray(payload?.categorias) ? payload.categorias : [],
    cursos: Array.isArray(payload?.cursos) ? payload.cursos : [],
    modulos: Array.isArray(payload?.modulos) ? payload.modulos : [],
    aulas: Array.isArray(payload?.aulas) ? payload.aulas : [],
  };
}
export async function fetchConteudosData() {
  const { snapshot } = await fetchConteudosState();
  return snapshot;
}
export async function fetchConteudoCategorias() {
  const response = await api.get("/conteudos/categorias");
  return Array.isArray(response?.data?.data) ? response.data.data : [];
}
export async function fetchConteudoCursosPaginated({
  page = 1,
  limit = 20,
  catId = "",
} = {}) {
  const params = {
    page,
    limit,
  };
  if (catId) {
    params.cat_id = catId;
  }
  const response = await api.get("/conteudos/cursos", {
    params,
  });
  const payload = response?.data?.data || {};
  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    pagination: {
      page: Number(payload?.pagination?.page || page),
      limit: Number(payload?.pagination?.limit || limit),
      total: Number(payload?.pagination?.total || 0),
      totalPages: Number(payload?.pagination?.total_pages || 1),
    },
  };
}
export async function createConteudoCategoria(data) {
  const response = await api.post("/conteudos/categorias", data);
  return response?.data?.data || null;
}
export async function updateConteudoCategoria(value, data) {
  const response = await api.put(
    `/conteudos/categorias?id=${encodeURIComponent(value)}`,
    data,
  );
  return response?.data?.data || null;
}
export async function deleteConteudoCategoria(value) {
  await api.delete(`/conteudos/categorias?id=${encodeURIComponent(value)}`);
}
export async function restoreConteudoCategoria(value) {
  await api.post(
    `/conteudos/categorias/restaurar?id=${encodeURIComponent(value)}`,
    {},
  );
}
export async function createConteudoCurso(data) {
  const response = await api.post("/conteudos/cursos", data);
  return response?.data?.data || null;
}
export async function updateConteudoCurso(value, data) {
  const response = await api.put(
    `/conteudos/cursos?id=${encodeURIComponent(value)}`,
    data,
  );
  return response?.data?.data || null;
}
export async function reorderConteudoCurso({ sourceId, targetId }) {
  const response = await api.post("/conteudos/cursos/reordenar", {
    source_id: sourceId,
    target_id: targetId,
  });
  return response?.data?.data || null;
}
export async function deleteConteudoCurso(value) {
  await api.delete(`/conteudos/cursos?id=${encodeURIComponent(value)}`);
}
export async function restoreConteudoCurso(value) {
  await api.post(
    `/conteudos/cursos/restaurar?id=${encodeURIComponent(value)}`,
    {},
  );
}
export async function createConteudoModulo(data) {
  const response = await api.post("/conteudos/modulos", data);
  return response?.data?.data || null;
}
export async function updateConteudoModulo(value, data) {
  const response = await api.put(
    `/conteudos/modulos?id=${encodeURIComponent(value)}`,
    data,
  );
  return response?.data?.data || null;
}
export async function reorderConteudoModulo({ sourceId, targetId }) {
  const response = await api.post("/conteudos/modulos/reordenar", {
    source_id: sourceId,
    target_id: targetId,
    sourceId,
    targetId,
  });
  return response?.data?.data || null;
}
export async function deleteConteudoModulo(value) {
  await api.delete(`/conteudos/modulos?id=${encodeURIComponent(value)}`);
}
export async function restoreConteudoModulo(value, data = {}) {
  await api.post(
    `/conteudos/modulos/restaurar?id=${encodeURIComponent(value)}`,
    data && typeof data === "object" ? data : {},
  );
}
export async function createConteudoAula(data) {
  const response = await api.post("/conteudos/aulas", data);
  return response?.data?.data || null;
}
export async function updateConteudoAula(value, data) {
  const response = await api.put(
    `/conteudos/aulas?id=${encodeURIComponent(value)}`,
    data,
  );
  return response?.data?.data || null;
}
export async function reorderConteudoAula({
  sourceId,
  targetId,
  targetModuloId,
}) {
  const response = await api.post("/conteudos/aulas/reordenar", {
    source_id: sourceId,
    target_id: targetId,
    sourceId,
    targetId,
    target_modulo_id: targetModuloId,
    targetModuloId,
  });
  return response?.data?.data || null;
}
export async function deleteConteudoAula(value) {
  await api.delete(`/conteudos/aulas?id=${encodeURIComponent(value)}`);
}
export async function restoreConteudoAula(value, data = {}) {
  await api.post(
    `/conteudos/aulas/restaurar?id=${encodeURIComponent(value)}`,
    data && typeof data === "object" ? data : {},
  );
}
export async function deleteConteudoBloco(value) {
  await api.delete(`/conteudos/blocos?id=${encodeURIComponent(value)}`);
}
export async function saveConteudosStructure(data) {
  const payload = normalizeConteudosData(data);
  await api.put("/conteudos/estrutura", {
    categorias: payload.categorias,
    cursos: payload.cursos,
    modulos: payload.modulos,
    aulas: payload.aulas,
  });
}
export async function fetchConteudosErros() {
  const response = await api.get("/conteudos/erros");
  return Array.isArray(response?.data?.data) ? response.data.data : [];
}
export async function saveConteudosErrors(erros) {
  await api.put("/conteudos/erros", {
    erros: Array.isArray(erros) ? erros : [],
  });
}
export async function saveConteudosProgress(progresso) {
  await api.post("/conteudos/progresso", {
    progresso: progresso && typeof progresso === "object" ? progresso : {},
  });
}
export async function uploadConteudoImage(file) {
  const payload = new FormData();
  payload.append("file", file);
  const response = await api.post("/conteudos/imagem", payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response?.data?.data || null;
}

// Envia um arquivo .html grande como arquivo estatico (evita guardar o
// conteudo inteiro em conteudo_guia_blocos.html / nas respostas JSON da API).
export async function uploadConteudoEmbedHtml(file) {
  const payload = new FormData();
  payload.append("file", file);
  const response = await api.post("/conteudos/embed-html", payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response?.data?.data || null;
}
export async function reportConteudoErro(erro) {
  await api.post("/conteudos/erros", erro);
}
export { getSnapshotFromResponse };
